import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface AnalyticsData {
  users: {
    total_users: number;
    new_users: number;
    time_period_days: number | null;
  };
  logins: {
    total_logins: number;
    unique_users: number;
    recurring_users: number;
    new_users: number;
    time_period_days: number | null;
  };
  lessons: {
    total_lessons: number;
    text_lessons: number;
    voice_lessons: number;
    ended_lessons: number;
    ongoing_lessons: number;
    avg_duration_minutes: number;
    avg_lessons_per_user: number;
    time_period_days: number | null;
  };
  subjects: {
    total_subjects: number;
    total_enrollments: number;
    users_with_subjects: number;
    avg_subjects_per_user: number;
    popular_subjects: Array<{ name: string; user_count: number }>;
    time_period_days: number | null;
  };
  engagement: {
    total_lessons: number;
    text_lessons: number;
    voice_lessons: number;
    text_percentage: number;
    voice_percentage: number;
    time_period_days: number | null;
  };
  generated_at: string;
}

const Analytics: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<number | null>(7);
  const [sendingReport, setSendingReport] = useState(false);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/login');
        return;
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const url = timeRange
        ? `${API_BASE_URL}/analytics/overview?days=${timeRange}`
        : `${API_BASE_URL}/analytics/overview`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 403) {
        setError('Admin access required');
        return;
      }

      if (response.status === 401) {
        navigate('/login');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const analyticsData = await response.json();
      setData(analyticsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const sendReport = async () => {
    setSendingReport(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/login');
        return;
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(
        `${API_BASE_URL}/analytics/send-report?days=${timeRange || 7}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send report');
      }

      alert('Analytics report sent successfully!');
    } catch (err) {
      alert('Failed to send report: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setSendingReport(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h2 className="text-red-800 font-semibold text-lg mb-2">Error</h2>
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 text-indigo-600 hover:text-indigo-800 font-medium"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const timeRangeName = timeRange ? `Last ${timeRange} Days` : 'All Time';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
              <p className="mt-1 text-sm text-gray-500">
                Generated at {new Date(data.generated_at).toLocaleString()}
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={sendReport}
                disabled={sendingReport}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                {sendingReport ? 'Sending...' : '📧 Send Report'}
              </button>
              <button
                onClick={() => navigate('/')}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                ← Back
              </button>
            </div>
          </div>

          {/* Time Range Selector */}
          <div className="mt-4 flex gap-2">
            {[7, 30, 90, null].map((days) => (
              <button
                key={days || 'all'}
                onClick={() => setTimeRange(days)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  timeRange === days
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {days ? `${days} Days` : 'All Time'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Total Users"
            value={data.users.total_users}
            subtitle={`+${data.users.new_users} new`}
            color="bg-gradient-to-br from-blue-500 to-blue-600"
            icon="👥"
          />
          <MetricCard
            title="Total Lessons"
            value={data.lessons.total_lessons}
            subtitle={`${data.lessons.avg_lessons_per_user} per user`}
            color="bg-gradient-to-br from-green-500 to-green-600"
            icon="📚"
          />
          <MetricCard
            title="Avg Duration"
            value={`${data.lessons.avg_duration_minutes.toFixed(1)}`}
            subtitle="minutes"
            color="bg-gradient-to-br from-purple-500 to-purple-600"
            icon="⏱️"
          />
          <MetricCard
            title="Active Users"
            value={data.logins.unique_users}
            subtitle={`${data.logins.recurring_users} recurring`}
            color="bg-gradient-to-br from-orange-500 to-orange-600"
            icon="🔥"
          />
        </div>

        {/* Detailed Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* User Statistics */}
          <StatsCard title="User Statistics" icon="👥">
            <StatRow label="Total Users" value={data.users.total_users} />
            <StatRow label="New Users" value={data.users.new_users} />
          </StatsCard>

          {/* Login Activity */}
          <StatsCard title="Login Activity" icon="🔐">
            <StatRow label="Total Logins" value={data.logins.total_logins} />
            <StatRow label="Unique Users" value={data.logins.unique_users} />
            <StatRow label="Recurring Users" value={data.logins.recurring_users} />
            <StatRow label="New Users" value={data.logins.new_users} />
          </StatsCard>

          {/* Lesson Activity */}
          <StatsCard title="Lesson Activity" icon="📚">
            <StatRow label="Total Lessons" value={data.lessons.total_lessons} />
            <StatRow label="Text Lessons" value={data.lessons.text_lessons} />
            <StatRow label="Voice Lessons" value={data.lessons.voice_lessons} />
            <StatRow label="Ended Lessons" value={data.lessons.ended_lessons} />
            <StatRow label="Ongoing Lessons" value={data.lessons.ongoing_lessons} />
            <StatRow
              label="Avg Duration"
              value={`${data.lessons.avg_duration_minutes.toFixed(1)} min`}
            />
            <StatRow
              label="Avg Lessons/User"
              value={data.lessons.avg_lessons_per_user.toFixed(2)}
            />
          </StatsCard>

          {/* Voice vs Text */}
          <StatsCard title="Voice vs Text Engagement" icon="🎤">
            <StatRow
              label="Text Lessons"
              value={`${data.engagement.text_lessons} (${data.engagement.text_percentage.toFixed(1)}%)`}
            />
            <StatRow
              label="Voice Lessons"
              value={`${data.engagement.voice_lessons} (${data.engagement.voice_percentage.toFixed(1)}%)`}
            />
            <div className="mt-4">
              <div className="flex h-4 overflow-hidden rounded-full">
                <div
                  className="bg-blue-500"
                  style={{ width: `${data.engagement.text_percentage}%` }}
                ></div>
                <div
                  className="bg-purple-500"
                  style={{ width: `${data.engagement.voice_percentage}%` }}
                ></div>
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-600">
                <span>Text {data.engagement.text_percentage.toFixed(1)}%</span>
                <span>Voice {data.engagement.voice_percentage.toFixed(1)}%</span>
              </div>
            </div>
          </StatsCard>

          {/* Subject Statistics */}
          <StatsCard title="Subject Statistics" icon="📖" className="lg:col-span-2">
            <div className="grid grid-cols-2 gap-4">
              <StatRow label="Total Subjects" value={data.subjects.total_subjects} />
              <StatRow label="Total Enrollments" value={data.subjects.total_enrollments} />
              <StatRow label="Users with Subjects" value={data.subjects.users_with_subjects} />
              <StatRow
                label="Avg Subjects/User"
                value={data.subjects.avg_subjects_per_user.toFixed(2)}
              />
            </div>

            {data.subjects.popular_subjects.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Popular Subjects</h4>
                <div className="space-y-2">
                  {data.subjects.popular_subjects.map((subject) => (
                    <div
                      key={subject.name}
                      className="flex justify-between items-center bg-gray-50 rounded-lg p-3"
                    >
                      <span className="font-medium text-gray-800">{subject.name}</span>
                      <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
                        {subject.user_count} users
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </StatsCard>
        </div>
      </div>
    </div>
  );
};

interface MetricCardProps {
  title: string;
  value: number | string;
  subtitle: string;
  color: string;
  icon: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, subtitle, color, icon }) => {
  return (
    <div className={`${color} rounded-xl shadow-lg p-6 text-white`}>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <p className="text-sm font-medium opacity-90">{title}</p>
          <p className="text-3xl font-bold mt-2">{value}</p>
          <p className="text-sm mt-1 opacity-75">{subtitle}</p>
        </div>
        <div className="text-4xl opacity-80">{icon}</div>
      </div>
    </div>
  );
};

interface StatsCardProps {
  title: string;
  icon: string;
  children: React.ReactNode;
  className?: string;
}

const StatsCard: React.FC<StatsCardProps> = ({ title, icon, children, className = '' }) => {
  return (
    <div className={`bg-white rounded-xl shadow p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <span>{icon}</span>
        {title}
      </h3>
      {children}
    </div>
  );
};

interface StatRowProps {
  label: string;
  value: number | string;
}

const StatRow: React.FC<StatRowProps> = ({ label, value }) => {
  return (
    <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
      <span className="text-gray-600 text-sm font-medium">{label}</span>
      <span className="text-gray-900 font-semibold">{value}</span>
    </div>
  );
};

export default Analytics;
